/**
 * A Standard MIDI File as what actually happened: notes with keys, times and lengths.
 *
 * Reading stops here, deliberately. This file knows the byte format and nothing about
 * notation -- no bars, no note values, no spelling -- because the two are separate
 * problems and only the second one involves guessing. `transcribe.ts` does the guessing,
 * and keeping it out of here means a malformed file fails as a parse and a badly played
 * one fails as a transcription, which are different sentences to show a user.
 *
 * Running status is supported because most files in the world use it: a channel message
 * may leave out its status byte and mean the last one. A file that opens with a data byte
 * has no status to run from, and is refused rather than read as something.
 */

import { cursorOf, has, readAscii, readBytes, readU16, readU32, readU8, readVlq } from "./bytes";
import type { Cursor } from "./bytes";

/** One sounded note, in the file's own ticks. */
export interface MidiNote {
  readonly channel: number;
  readonly key: number;
  /** 1 to 127, as struck. */
  readonly velocity: number;
  readonly start: number;
  readonly ticks: number;
}

export interface MidiTrack {
  readonly name: string | null;
  /** The General MIDI program this track last asked for, or `null` when it asked for none. */
  readonly program: number | null;
  readonly notes: readonly MidiNote[];
}

export interface MidiTempoMark {
  readonly tick: number;
  readonly microsPerQuarter: number;
}

export interface MidiTimeMark {
  readonly tick: number;
  readonly beats: number;
  readonly beatType: number;
}

export interface MidiKeyMark {
  readonly tick: number;
  readonly fifths: number;
  readonly minor: boolean;
}

/** A whole file, read but not yet understood as music. */
export interface MidiFile {
  /** Ticks per quarter note, as the file counts them. */
  readonly division: number;
  readonly title: string | null;
  readonly tracks: readonly MidiTrack[];
  readonly tempos: readonly MidiTempoMark[];
  readonly times: readonly MidiTimeMark[];
  readonly keys: readonly MidiKeyMark[];
}

export type MidiRead =
  | { readonly ok: true; readonly file: MidiFile }
  | { readonly ok: false; readonly reason: string };

/** Everything a track can contribute, collected as it is read. */
interface TrackRead {
  readonly track: MidiTrack;
  readonly tempos: readonly MidiTempoMark[];
  readonly times: readonly MidiTimeMark[];
  readonly keys: readonly MidiKeyMark[];
}

/** A note waiting for the note-off that ends it. */
interface Sounding {
  readonly channel: number;
  readonly key: number;
  readonly velocity: number;
  readonly start: number;
}

function readMeta(
  cursor: Cursor,
  tick: number,
  into: {
    name: string | null;
    tempos: MidiTempoMark[];
    times: MidiTimeMark[];
    keys: MidiKeyMark[];
  },
): void {
  const kind = readU8(cursor);
  const length = readVlq(cursor);
  const at = cursor.at;
  const body = readBytes(cursor, length);
  cursor.at = at + length;

  if (kind === 0x03 && into.name === null) {
    into.name = Array.from(body, (byte) => String.fromCharCode(byte))
      .join("")
      .trim();
    return;
  }
  if (kind === 0x51 && body.length >= 3) {
    const micros = ((body[0] ?? 0) << 16) | ((body[1] ?? 0) << 8) | (body[2] ?? 0);
    if (micros > 0) into.tempos.push({ tick, microsPerQuarter: micros });
    return;
  }
  if (kind === 0x58 && body.length >= 2) {
    into.times.push({ tick, beats: body[0] ?? 4, beatType: 2 ** (body[1] ?? 2) });
    return;
  }
  if (kind === 0x59 && body.length >= 2) {
    const fifths = (body[0] ?? 0) > 127 ? (body[0] ?? 0) - 256 : (body[0] ?? 0);
    into.keys.push({ tick, fifths, minor: (body[1] ?? 0) === 1 });
  }
}

/**
 * One track chunk, from its first delta time to its end.
 *
 * A note-off is either a note-off or a note-on struck at velocity zero -- both are ordinary
 * and both end a note. Two notes of the same key sounding at once on one channel are ended
 * oldest first, which is what a sequencer does and the only reading that cannot leave a
 * note hanging.
 */
function readTrack(cursor: Cursor, length: number): TrackRead {
  const end = cursor.at + length;
  const collected = {
    name: null as string | null,
    tempos: [] as MidiTempoMark[],
    times: [] as MidiTimeMark[],
    keys: [] as MidiKeyMark[],
  };
  const notes: MidiNote[] = [];
  const sounding: Sounding[] = [];
  let program: number | null = null;
  let tick = 0;
  let status = 0;

  const stop = (channel: number, key: number, at: number): void => {
    const found = sounding.findIndex((one) => one.channel === channel && one.key === key);
    const note = found < 0 ? undefined : sounding[found];
    if (note === undefined) return;
    sounding.splice(found, 1);
    notes.push({
      channel,
      key,
      velocity: note.velocity,
      start: note.start,
      ticks: Math.max(at - note.start, 0),
    });
  };

  while (cursor.at < end && has(cursor, 1)) {
    tick += readVlq(cursor);
    let byte = readU8(cursor);
    if (byte < 0x80) {
      cursor.at -= 1;
      byte = status;
    }
    if (byte >= 0x80 && byte < 0xf0) status = byte;

    const kind = byte & 0xf0;
    const channel = byte & 0x0f;

    if (byte === 0xff) {
      readMeta(cursor, tick, collected);
      continue;
    }
    if (byte === 0xf0 || byte === 0xf7) {
      cursor.at += readVlq(cursor);
      continue;
    }
    if (kind === 0x90) {
      const key = readU8(cursor);
      const velocity = readU8(cursor);
      if (velocity === 0) stop(channel, key, tick);
      else sounding.push({ channel, key, velocity, start: tick });
      continue;
    }
    if (kind === 0x80) {
      const key = readU8(cursor);
      readU8(cursor);
      stop(channel, key, tick);
      continue;
    }
    if (kind === 0xc0) {
      program = readU8(cursor);
      continue;
    }
    if (kind === 0xd0) {
      readU8(cursor);
      continue;
    }
    if (kind === 0xa0 || kind === 0xb0 || kind === 0xe0) {
      cursor.at += 2;
      continue;
    }
    // Nothing else can be sized, so the track cannot be walked any further.
    break;
  }

  // A file may simply stop with notes held; they are ended where the track ends rather
  // than thrown away, because a held note is music and a lost one is a hole.
  for (const held of [...sounding]) stop(held.channel, held.key, tick);
  cursor.at = end;

  const name = collected.name === null || collected.name === "" ? null : collected.name;
  return {
    track: { name, program, notes: notes.sort((left, right) => left.start - right.start) },
    tempos: collected.tempos,
    times: collected.times,
    keys: collected.keys,
  };
}

/** The bytes of a `.mid` file, read into notes and marks. */
export function readMidi(data: Uint8Array): MidiRead {
  const cursor = cursorOf(data);
  if (!has(cursor, 14) || readAscii(cursor, 4) !== "MThd") {
    return { ok: false, reason: "That is not a MIDI file: it does not begin with a header chunk." };
  }
  const headerLength = readU32(cursor);
  readU16(cursor);
  const declared = readU16(cursor);
  const division = readU16(cursor);
  cursor.at += Math.max(headerLength - 6, 0);

  if (division === 0 || (division & 0x8000) !== 0) {
    return {
      ok: false,
      reason: "This file is timed in frames of film rather than in beats, which has no bars in it.",
    };
  }

  const tracks: MidiTrack[] = [];
  const tempos: MidiTempoMark[] = [];
  const times: MidiTimeMark[] = [];
  const keys: MidiKeyMark[] = [];
  let title: string | null = null;

  while (has(cursor, 8)) {
    const tag = readAscii(cursor, 4);
    const length = readU32(cursor);
    if (tag !== "MTrk") {
      cursor.at += length;
      continue;
    }
    const read = readTrack(cursor, length);
    tempos.push(...read.tempos);
    times.push(...read.times);
    keys.push(...read.keys);
    if (read.track.notes.length === 0) {
      if (title === null) title = read.track.name;
      continue;
    }
    tracks.push(read.track);
  }

  if (tracks.length === 0) {
    return {
      ok: false,
      reason:
        declared === 0
          ? "This file has no notes in it."
          : "This file has tracks but no notes in them.",
    };
  }

  const order = (marks: { tick: number }[]): void => {
    marks.sort((left, right) => left.tick - right.tick);
  };
  order(tempos);
  order(times);
  order(keys);

  return { ok: true, file: { division, title, tracks, tempos, times, keys } };
}
